import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { getErrorPresentation } from "@/shared/api/error-presentation";
import type { UnifiedError } from "@/shared/types/api";

import { mineQueryKeys } from "../query-options";
import { useSortBy, useSortOrder } from "../store";
import type { Item } from "../types";
import { useMineItemsInfiniteQuery } from "./use-mine-items-infinite-query";
import { useWorkspaceContext } from "../workspace/use-workspace-context";

export function useMineItemsData() {
  const workspaceContext = useWorkspaceContext();
  const sortBy = useSortBy();
  const sortOrder = useSortOrder();
  const queryClient = useQueryClient();
  const query = useMineItemsInfiniteQuery();

  const items = useMemo<Item[]>(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data],
  );

  const loading =
    query.isPending || query.isFetchingNextPage || query.isRefetching;
  const hasMore = query.hasNextPage ?? false;
  const error = useMemo(() => {
    if (!query.error) {
      return null;
    }

    if (typeof query.error === "object" && query.error !== null && "code" in query.error) {
      return getErrorPresentation(query.error as UnifiedError);
    }

    if (query.error instanceof Error) {
      return {
        title: "暂时无法加载内容",
        description: query.error.message,
      };
    }

    return {
      title: "暂时无法加载内容",
      description: "请求未能完成，请稍后重试。",
    };
  }, [query.error]);

  const loadMore = useCallback(() => {
    if (!query.hasNextPage || query.isFetchingNextPage) {
      return;
    }

    return query.fetchNextPage();
  }, [query]);

  const refresh = useCallback(() => {
    void queryClient.resetQueries({
      queryKey: mineQueryKeys.itemsList({
        workspace: workspaceContext,
        sortBy,
        sortOrder,
      }),
      exact: true,
    });
  }, [queryClient, sortBy, sortOrder, workspaceContext]);

  return {
    items,
    loading,
    hasMore,
    error,
    loadMore,
    refresh,
  };
}
